import { useState, useRef, useMemo } from "react"
import { Formik, Field, Form } from "formik"
import * as Yup from "yup"
import {
    InputGroup,
    FieldWrappers,
    Label,
    Wrapper,
    CombineInputGroup,
} from "../../Styles/InputStyles"
import { toast } from "react-toastify"
import { MdEdit } from "react-icons/md"
import tw from "tailwind-styled-components"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import "react-dropdown/style.css"
import { FormControl } from "@mui/material"
import { useSelector } from "react-redux"
import SelectLocationModal from "../../SelectLocationModal"

const PropertyDetailModal = ({
    editNameLoading,
    editNameMutate,
    apiData
}) => {
    const [showLocationModal, setShowLocationModal] = useState(false);
    const locations = useSelector((state) => state.LocationsReducer.locations);
    const filteredLocations = useMemo(() => {
        return locations?.filter(location => location.id !== 0) || [];
    }, [locations]);
    const formikRef = useRef(null)

    const perfectFit = [
        { value: 1, label: "Male" },
        { value: 2, label: "Female" },
        { value: 3, label: "Both" },
    ];
    const propertyComesThrough = [
        { value: 1, label: "Self" },
        { value: 2, label: "News Paper" },
        { value: 3, label: "Broker" },
        { value: 4, label: "Portal" },
    ];
    const rentalCycle = [
        { label: "1st to 1st", value: "01" },
        { label: "15th to 15th", value: "15" },
        { label: "Move-In Date", value: "00" },
    ];
    const propertyType = [
        { value: 1, label: "PG/Hostel/Co-Living" },
        { value: 2, label: "Flat/Apartment" },
    ];

    const validationSchema = Yup.object({
        address: Yup.string()
            .required("Address is required")
            .min(5, "Address must be at least 5 characters"),
        pincode: Yup.string()
            .test(
                "pincode-format",
                "Pincode must be 6 digits",
                (value) => !value || /^\d{6}$/.test(value)
            )
            .nullable(),
    });

    const initialValues = {
        propertyName: apiData?.property?.name || "",
        perfectFit: apiData?.property?.tenantPreference || "",
        securityDeposit: apiData?.property?.security || "",
        agreementPeriod: apiData?.property?.agreementPeriod || "",
        noticePeriod: apiData?.property?.noticePeriod || "",
        lockInPeriod: apiData?.property?.lockInPeriod || "",
        id: apiData?.property?.id,
        locationId: apiData?.property?.locationId || "",
        address: apiData?.property?.address || "",
        pincode: apiData?.property?.pincode || "",
        propertyType: apiData?.property?.type || "",
        propertyCome: apiData?.property?.source || "",
        rentalCycle: apiData?.property?.rentalCycle || "",
    };

    const handleSubmit = (values) => {
        const propertyNameRegex = /^[a-zA-Z0-9\-]+(?: [a-zA-Z0-9\-]+)*$/;
        const securityDepositRegex = /^\d{1,6}$/;
        const maxSecurityDeposit = 1000000;
        // if (
        //     !values.lockInPeriod ||
        //     !values.noticePeriod ||
        //     !values.agreementPeriod
        // ) {
        //     toast.error("All required fields must be filled");
        //     return;
        // }
        if (
            values.lockInPeriod == null ||
            values.noticePeriod == null ||
            values.agreementPeriod == null
        ) {
            toast.error("All required fields must be filled");
            return;
        }
        if (!values.address || values.address.trim().length < 5) {
            toast.error("Address must be at least 5 characters long");
            return;
        }

        if (
            !propertyNameRegex.test(values.propertyName.trim()) ||
            values.propertyName.trim().length < 5
        ) {
            toast.error(
                "Property Name should contain only alphabets, numbers, spaces, and dashes (-), and be at least 5 characters long"
            );
            return;
        }

        if (
            (values.securityDeposit &&
                !securityDepositRegex.test(values.securityDeposit)) ||
            parseInt(values.securityDeposit) > maxSecurityDeposit
        ) {
            toast.error(
                "Security deposit must be between 1 and 10,00,000 and contain only numbers"
            );
            return;
        }

        if (!values.rentalCycle) {
            toast.error("Rental Cycle is required");
            return;
        }

        editNameMutate({
            name: values.propertyName,
            id: apiData?.property?.id,
            tenantPreference: values.perfectFit,
            security: Number(values.securityDeposit || 0),
            agreementPeriod: values.agreementPeriod,
            noticePeriod: values.noticePeriod,
            lockInPeriod: values.lockInPeriod,
            locationId: values.locationId,
            address: values.address,
            pincode: values.pincode || null,
            type: values.propertyType,
            source: values.propertyCome || "",
            rentalCycle: String(values.rentalCycle),
        });
    }

    const getInputClass = (isDisabled) => {
        return isDisabled
            ? "bg-gray-100 cursor-not-allowed" : "pl-2 relative rounded-md w-full flex items-center border border-gray-400 h-12";
    };

    return (
        <Wrapper className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm space-y-2">
            <div className="flex items-start justify-between">

                {/* LEFT */}
                <div>
                    <p className="text-base font-semibold text-gray-900">
                        Property Details
                    </p>
                    <p className="text-sm text-gray-500">
                        Manage basic information and rental configuration
                    </p>
                </div>
            </div>
            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
                enableReinitialize={true}
            >
                {({ values, setFieldValue, handleChange, handleBlur, errors, touched }) => {
                    const getLocationLabel = () => {
                        const selectedRole = filteredLocations.find(
                            (role) => role.id === values?.locationId,
                        );
                        return selectedRole ? selectedRole.name : "Select Location";
                    };

                    const handleLocationSelect = (selectedValue) => {
                        setFieldValue("locationId", selectedValue);
                        setFieldValue("locationTouched", true);
                    };

                    return (
                        <Form>
                            <Wrapper>
                                <CombineInputGroup>
                                    <InputGroup>
                                        <Label htmlFor="propertyName">Property Name *</Label>
                                        <FieldWrapper>
                                            <Field
                                                type="text"
                                                name="propertyName"
                                                id="propertyName"
                                                autoComplete="off"
                                                required
                                                className={getInputClass(false)}
                                            />
                                        </FieldWrapper>
                                    </InputGroup>
                                    <InputGroup>
                                        <Label htmlFor="location">Location </Label>
                                        <FieldWrappers onClick={() => setShowLocationModal(true)} className="border border-gray-400 rounded-md">
                                            <Field
                                                type="text"
                                                name="locationId"
                                                id="locationId"
                                                autoComplete="off"
                                                readOnly
                                                value={getLocationLabel()}
                                                className="w-full border-none outline-none bg-transparent cursor-pointer"
                                            />
                                            <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                        </FieldWrappers>

                                        {touched.locationId && errors.locationId && (
                                            <p className="text-rose-600 text-sm mt-1">
                                                {errors.locationId}
                                            </p>
                                        )}
                                    </InputGroup>
                                    <InputGroup>
                                        <Label>A Perfect Fit For </Label>
                                        <FormControl fullWidth>
                                            <Select
                                                className="px-2"
                                                id="perfectFit"
                                                name="perfectFit"
                                                value={values.perfectFit}
                                                onChange={(e) =>
                                                    setFieldValue("perfectFit", e.target.value)
                                                }
                                                onBlur={handleBlur}
                                                sx={{
                                                    height: "48px",
                                                    borderRadius: "6px",
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: "#a3a3b9",
                                                    },
                                                }}
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    if (!selected) {
                                                        return <span className="text-gray-400">Select gender preference</span>;
                                                    }
                                                    const option = perfectFit.find(opt => opt.value === selected);
                                                    return option ? option.label : selected;
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <span className="text-gray-400">Select gender preference</span>
                                                </MenuItem>
                                                {perfectFit.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {touched.perfectFit && errors.perfectFit && (
                                            <p className="text-rose-600 text-sm">
                                                {errors.perfectFit}
                                            </p>
                                        )}
                                    </InputGroup>
                                </CombineInputGroup>

                                <CombineInputGroup>

                                    <InputGroup>
                                        <Label htmlFor="securityDeposit">Security Deposit </Label>
                                        <FieldWrapper>
                                            <Field
                                                type="number"
                                                name="securityDeposit"
                                                id="securityDeposit"
                                                autoComplete="off"
                                                className={getInputClass(false)}
                                            />
                                        </FieldWrapper>
                                    </InputGroup>
                                    <InputGroup>
                                        <Label>Rental Cycle *</Label>
                                        <FormControl fullWidth>
                                            <Select
                                                className="px-2"
                                                id="rentalCycle"
                                                name="rentalCycle"
                                                value={values.rentalCycle}
                                                onChange={(e) =>
                                                    setFieldValue("rentalCycle", e.target.value)
                                                }
                                                onBlur={handleBlur}
                                                required
                                                sx={{
                                                    height: "48px",
                                                    borderRadius: "6px",
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: "#a3a3b9",
                                                    },
                                                }}
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    if (!selected) {
                                                        return <span className="text-gray-400">Select rental cycle</span>;
                                                    }
                                                    const option = rentalCycle.find(opt => opt.value === selected);
                                                    return option ? option.label : selected;
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <span className="text-gray-400">Select rental cycle</span>
                                                </MenuItem>
                                                {rentalCycle.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                    </InputGroup>
                                    <InputGroup>
                                        <Label>Property Source</Label>
                                        <FormControl fullWidth>
                                            <Select
                                                className="px-2"
                                                id="propertyCome"
                                                name="propertyCome"
                                                value={values.propertyCome}
                                                onChange={(e) =>
                                                    setFieldValue("propertyCome", e.target.value)
                                                }
                                                onBlur={handleBlur}
                                                sx={{
                                                    height: "48px",
                                                    borderRadius: "6px",
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: "#a3a3b9",
                                                    }
                                                }}
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    if (!selected) {
                                                        return <span className="text-gray-400">Select property source</span>;
                                                    }
                                                    const option = propertyComesThrough.find(opt => opt.value === selected);
                                                    return option ? option.label : selected;
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <span className="text-gray-400">Select property source</span>
                                                </MenuItem>
                                                {propertyComesThrough.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                    </InputGroup>

                                </CombineInputGroup>

                                <CombineInputGroup>

                                    <InputGroup>
                                        <Label htmlFor="agreementPeriod">Agreement Period *</Label>
                                        <FormControl fullWidth>
                                            <Select
                                                className="px-2"
                                                id="agreementPeriod"
                                                name="agreementPeriod"
                                                value={values.agreementPeriod}
                                                onChange={(e) =>
                                                    setFieldValue("agreementPeriod", e.target.value)
                                                }
                                                required
                                                sx={{
                                                    height: "48px",
                                                    borderRadius: "6px",
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: "#a3a3b9",
                                                    }
                                                }}
                                            >
                                                {Array.from({ length: 12 }, (_, i) => i + 1).map(
                                                    (month) => (
                                                        <MenuItem key={month} value={month}>
                                                            {month} {month === 1 ? "Month" : "Months"}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        </FormControl>
                                    </InputGroup>
                                    <InputGroup>
                                        <Label htmlFor="noticePeriod">Notice Period *</Label>
                                        <FormControl fullWidth>
                                            <Select
                                                className="px-2"
                                                id="noticePeriod"
                                                name="noticePeriod"
                                                value={values.noticePeriod}
                                                onChange={(e) =>
                                                    setFieldValue("noticePeriod", e.target.value)
                                                }
                                                required
                                                sx={{
                                                    height: "48px",
                                                    borderRadius: "6px",
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: "#a3a3b9",
                                                    }
                                                }}
                                            >
                                                {/* {Array.from({ length: 6 }, (_, i) => i + 1).map((month) => (
                                                    <MenuItem key={month} value={month}>
                                                        {month} {month === 1 ? "Month" : "Months"}
                                                    </MenuItem>
                                                ))} */}
                                                {Array.from({ length: 7 }, (_, i) => i).map((month) => (
                                                    <MenuItem key={month} value={month}>
                                                        {month} {month === 1 ? "Month" : "Months"}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </InputGroup>
                                    <InputGroup>
                                        <Label htmlFor="lockInPeriod">Lock-In Period *</Label>
                                        <FormControl fullWidth>
                                            <Select
                                                className="px-2"
                                                id="lockInPeriod"
                                                name="lockInPeriod"
                                                value={values.lockInPeriod}
                                                onChange={(e) =>
                                                    setFieldValue("lockInPeriod", e.target.value)
                                                }
                                                required
                                                sx={{
                                                    height: "48px",
                                                    borderRadius: "6px",
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: "#a3a3b9",
                                                    }
                                                }}
                                            >
                                                {Array.from({ length: 11 }, (_, i) => i + 1).map(
                                                    (month) => (
                                                        <MenuItem key={month} value={month}>
                                                            {month} {month === 1 ? "Month" : "Months"}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        </FormControl>
                                    </InputGroup>
                                </CombineInputGroup>
                                <AddressGrid>
                                    <AddressInput>
                                        <InputGroup>
                                            <Label htmlFor="address">Address *</Label>
                                            <FieldWrapper>
                                                <Field
                                                    type="text"
                                                    name="address"
                                                    id="address"
                                                    autoComplete="off"
                                                    required
                                                    className={getInputClass(false)}
                                                    placeholder="Enter full address (minimum 10 characters)"
                                                />
                                            </FieldWrapper>
                                            {touched.address && errors.address && (
                                                <p className="text-rose-600 text-sm mt-1">
                                                    {errors.address}
                                                </p>
                                            )}
                                        </InputGroup>
                                    </AddressInput>
                                    <PincodeInput>
                                        <InputGroup>
                                            <Label htmlFor="pincode">Pincode</Label>
                                            <FieldWrapper>
                                                <Field
                                                    type="text"
                                                    name="pincode"
                                                    id="pincode"
                                                    autoComplete="off"
                                                    className={getInputClass(false)}
                                                    placeholder="6-digit pincode"
                                                    maxLength="6"
                                                />
                                            </FieldWrapper>
                                            {touched.pincode && errors.pincode && (
                                                <p className="text-rose-600 text-sm mt-1">
                                                    {errors.pincode}
                                                </p>
                                            )}
                                        </InputGroup>
                                    </PincodeInput>
                                </AddressGrid>
                            </Wrapper>

                            <BtnWrapper>
                                <SubmitBtn type="submit" disabled={editNameLoading}>
                                    {editNameLoading ? "Updating..." : "Update"}
                                </SubmitBtn>
                            </BtnWrapper>
                            <SelectLocationModal
                                isOpen={showLocationModal}
                                onClose={() => setShowLocationModal(false)}
                                options={filteredLocations}
                                title="Select Location"
                                selectedValue={values.locationId}
                                onSelect={handleLocationSelect}
                                searchPlaceholder="Search Location..."
                                width="1/3"
                            />
                        </Form>
                    )
                }}
            </Formik>
        </Wrapper>
    )
}

const FieldWrapper = tw.div`w-full`;
const BtnWrapper = tw.div` pt-4 flex justify-end`;
const SubmitBtn = tw.button`
text-sm  w-24  md:w-20 h-7 grid place-items-center disabled:opacity-50 disabled:cursor-not-allowed  md:text-xs bg-primary-color hover:bg-hover-color text-white rounded-md shadow-md`;
const AddressGrid = tw.div`grid md:grid-cols-3 grid-cols-1 gap-4`
const AddressInput = tw.div`grid md:col-span-2 col-span-1`
const PincodeInput = tw.div`col-span-1`
export default PropertyDetailModal